import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { produce, type Draft } from 'immer'
import isError from 'lodash/isError'
import { wireValidationErrorMapFromTrpcError } from '@klear/schema/wire/trpc-form-errors'
import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import { hasEntityFormErrors } from '@klear/ui/entity-form-state'

export type UseEntityWizardFormOptions<
  Values extends Record<string, unknown>,
  Dirty extends Record<string, unknown>,
  Path extends string,
  FieldValue,
  StepId extends string
> = {
  defaultValues: Values
  setField: (draft: Draft<Values>, path: Path, value: FieldValue) => void
  markDirtyPaths: (draft: Draft<Dirty>, paths: Path | readonly Path[]) => void
  clearErrors: (errors: EntityFormErrorMap, paths: Path | readonly Path[]) => EntityFormErrorMap
  stepFields: (stepId: StepId) => readonly Path[]
  validateStep: (stepId: StepId, values: Values) => EntityFormErrorMap | Promise<EntityFormErrorMap>
  saveErrorFallback: string
}

export type EntityWizardForm<
  Values extends Record<string, unknown>,
  Dirty extends Record<string, unknown>,
  Path extends string,
  FieldValue,
  StepId extends string
> = {
  values: Values
  dirtyFields: Dirty
  errors: EntityFormErrorMap
  setErrors: Dispatch<SetStateAction<EntityFormErrorMap>>
  saveError: string | null
  updateValues: (
    recipe: (draft: Draft<Values>) => void | Path | readonly Path[] | undefined,
    dirtyPaths?: Path | readonly Path[]
  ) => void
  handleFieldChange: (path: Path, value: FieldValue) => void
  validateBeforeNext: (stepId: StepId) => Promise<boolean>
  runSubmit: (action: () => Promise<void>) => Promise<void>
  clearDirty: () => void
  resetValues: (next: Values) => void
}

export function useEntityWizardForm<
  Values extends Record<string, unknown>,
  Dirty extends Record<string, unknown>,
  Path extends string,
  FieldValue,
  StepId extends string
>(
  options: UseEntityWizardFormOptions<Values, Dirty, Path, FieldValue, StepId>
): EntityWizardForm<Values, Dirty, Path, FieldValue, StepId> {
  const optionsRef = useRef(options)
  optionsRef.current = options

  const [values, setValues] = useState<Values>(options.defaultValues)
  const [dirtyFields, setDirtyFields] = useState<Dirty>({} as Dirty)
  const [errors, setErrors] = useState<EntityFormErrorMap>({})
  const [saveError, setSaveError] = useState<string | null>(null)

  const valuesRef = useRef(values)
  valuesRef.current = values

  const updateValues = useCallback(
    (
      recipe: (draft: Draft<Values>) => void | Path | readonly Path[] | undefined,
      dirtyPaths?: Path | readonly Path[]
    ) => {
      let returnedPaths: Path | readonly Path[] | undefined
      setValues((current) =>
        produce(current, (draft) => {
          const result = recipe(draft)
          if (result !== undefined) returnedPaths = result
        })
      )
      const paths = dirtyPaths ?? returnedPaths
      if (paths) {
        setDirtyFields((current) => produce(current, (draft) => optionsRef.current.markDirtyPaths(draft, paths)))
        setErrors((current) => optionsRef.current.clearErrors(current, paths))
      }
    },
    []
  )
  const handleFieldChange = useCallback(
    (path: Path, value: FieldValue) => {
      updateValues((draft) => optionsRef.current.setField(draft, path, value), path)
    },
    [updateValues]
  )

  const validateBeforeNext = useCallback(async (stepId: StepId): Promise<boolean> => {
    const { validateStep, stepFields, clearErrors } = optionsRef.current
    const stepErrors = await validateStep(stepId, valuesRef.current)
    if (hasEntityFormErrors(stepErrors)) {
      setErrors((current) => ({ ...current, ...stepErrors }))
      return false
    }
    const fields = stepFields(stepId)
    if (fields.length > 0) setErrors((current) => clearErrors(current, fields))
    return true
  }, [])

  const runSubmit = useCallback(async (action: () => Promise<void>): Promise<void> => {
    setSaveError(null)
    try {
      await action()
    } catch (err: unknown) {
      const validationErrorMap = wireValidationErrorMapFromTrpcError(err)
      if (validationErrorMap) {
        setErrors(validationErrorMap)
      } else {
        setSaveError(isError(err) ? err.message : optionsRef.current.saveErrorFallback)
      }
    }
  }, [])

  const clearDirty = useCallback(() => {
    setDirtyFields({} as Dirty)
  }, [])

  const resetValues = useCallback((next: Values) => {
    setValues(next)
    setDirtyFields({} as Dirty)
    setErrors({})
    setSaveError(null)
  }, [])

  return {
    values,
    dirtyFields,
    errors,
    setErrors,
    saveError,
    updateValues,
    handleFieldChange,
    validateBeforeNext,
    runSubmit,
    clearDirty,
    resetValues
  }
}
